import React from 'react';
import { InputNumber, Select } from 'antd';

import ToggleRow from '../common/ToggleRow';
import { optionSearchFilter } from '../Input';
import { LIMITS } from '../../utils/analytics';
import BuilderStep from './BuilderStep';
import { MULTI_WORDS } from './builderModel';
import { Field, SegmentedField } from './controls';
import FieldMessage, { explainedOptions, messageProps } from './FieldMessage';

/** The group select's value for no group. */
export const NO_GROUP = '__none';

const BINS_MODES = [
	{ label: 'Number of ranges', value: 'count' },
	{ label: 'Range size', value: 'size' },
];

/**
 * Steps 3 and 4, Group by and Split by: the element the chart lays out
 * along its axis, and the one it colours each bar by. Only what the
 * capability table lets the element group by is offered; for a date, its
 * bucket (a day of the week, an hour of the day and a month of the year
 * among them, now KQL can open those bars — PR AK; a part the table holds
 * back would be listed, disabled, with its reason); "Created by" groups as a
 * user does;
 * for a number, an amount or a measurement, its ranges — ten equal ones
 * until the person picks a count or a size (the server has no default,
 * `bins_required`); for a lookup, a user or free text, how many values to
 * keep before the rest fold into Other; for an element holding several
 * dates, how to read it (ruled d06) and one line on which reading can open
 * a list; and whether items with no value show as Not set.
 *
 *   which   'groupBy' | 'splitBy'
 */
export default function GroupStep({ which, number, draft, view, onGroup, onField, messagesFor, step }) {
	const isSplit = which === 'splitBy';
	const title = isSplit ? 'Split by' : 'Group by';
	const g = isSplit ? view.split : view.group;
	const group = draft[which];
	const base = `chart-builder-${isSplit ? 'split' : 'group'}`;
	const current = g.current;
	const onMessages = messagesFor(`${which}.on`);

	if (isSplit && !draft.groupBy) {
		return (
			<BuilderStep step={which} number={number} title={title} {...step}>
				<p className='chart-builder__hint chart-builder__hint--block'>Choose a Group by first. Split by then gives each bar one color per value of a second element.</p>
			</BuilderStep>
		);
	}

	const options = [{ value: NO_GROUP, label: 'Nothing', display: 'Nothing', searchText: 'Nothing' }, ...explainedOptions(g.options)];
	const label = current ? current.field.label : '';

	return (
		<BuilderStep step={which} number={number} title={title} {...step}>
			<Field id={`${base}-on`} messages={onMessages}>
				<Select
					id={`${base}-on`}
					virtual={false}
					className='chart-builder__select'
					aria-label={title}
					value={group ? group.on : NO_GROUP}
					options={options}
					optionLabelProp='display'
					onChange={(value) => onField(which, value === NO_GROUP ? null : value)}
					showSearch
					filterOption={optionSearchFilter}
					popupMatchSelectWidth={false}
					{...messageProps(onMessages, `${base}-on-messages`)}
				/>
			</Field>
			{current && current.buckets ? (
				<Field id={`${base}-bucket`} label='By' messages={messagesFor(`${which}.bucket`)}>
					<Select
						id={`${base}-bucket`}
						virtual={false}
						className='chart-builder__select'
						value={group.bucket || undefined}
						options={explainedOptions(current.buckets)}
						optionLabelProp='display'
						onChange={(bucket) => onGroup(which, { bucket })}
						popupMatchSelectWidth={false}
						{...messageProps(messagesFor(`${which}.bucket`), `${base}-bucket-messages`)}
					/>
				</Field>
			) : null}
			{current && current.multi ? (
				<SegmentedField
					id={`${base}-multi`}
					label={`${label} can hold several dates`}
					value={group.multi || 'earliest'}
					options={current.multi.map((reading) => ({ label: MULTI_WORDS[reading] || reading, value: reading }))}
					onChange={(multi) => onGroup(which, { multi })}
					messages={messagesFor(`${which}.multi`)}
					hint={current.door ? current.door.text : null}
				/>
			) : null}
			{current && !current.multi && current.door ? <div className='chart-builder__hint'>{current.door.text}</div> : null}
			{current && current.bins ? (
				<>
					<SegmentedField
						id={`${base}-bins-mode`}
						label='Ranges'
						value={group.binsMode}
						options={BINS_MODES}
						onChange={(binsMode) => onGroup(which, { binsMode })}
					/>
					<Field
						id={`${base}-bins`}
						label={group.binsMode === 'size' ? 'Each range is' : 'How many ranges'}
						messages={messagesFor(`${which}.bins`)}
						hint={group.binsMode === 'size' && current.bins.unit ? `Typed in ${current.bins.unit.text}.` : (group.binsMode === 'count' ? 'Equal ranges, from the smallest value to the largest.' : null)}
					>
						{group.binsMode === 'size' ? (
							<InputNumber
								id={`${base}-bins`}
								className='chart-builder__number'
								min={0}
								value={group.binsSize}
								onChange={(value) => onGroup(which, { binsSize: typeof value === 'number' && value > 0 ? value : null })}
								{...messageProps(messagesFor(`${which}.bins`), `${base}-bins-messages`)}
							/>
						) : (
							<InputNumber
								id={`${base}-bins`}
								className='chart-builder__number'
								min={1}
								max={LIMITS.buckets}
								precision={0}
								value={group.binsCount}
								onChange={(value) => onGroup(which, { binsCount: typeof value === 'number' ? value : null })}
								{...messageProps(messagesFor(`${which}.bins`), `${base}-bins-messages`)}
							/>
						)}
					</Field>
				</>
			) : null}
			{current && current.top ? (
				<Field
					id={`${base}-top`}
					label='Values shown'
					messages={messagesFor(`${which}.top`)}
					hint={`Up to ${current.top.max}; any more are one bar, Other.`}
				>
					<InputNumber
						id={`${base}-top`}
						className='chart-builder__number'
						min={1}
						max={current.top.max}
						precision={0}
						placeholder={current.top.defaultValue ? String(current.top.defaultValue) : 'All'}
						value={group.top}
						onChange={(value) => onGroup(which, { top: typeof value === 'number' ? value : null })}
					/>
				</Field>
			) : null}
			{current && current.blanks ? (
				<>
					<ToggleRow
						id={`${base}-blanks`}
						label='Show items with no value'
						description={`As a bar of their own, Not set, when some items have no ${label}.`}
						checked={group.blanks !== 'hide'}
						onChange={(on) => onGroup(which, { blanks: on ? 'show' : 'hide' })}
					/>
					<FieldMessage messages={messagesFor(`${which}.blanks`)} id={`${base}-blanks-messages`} />
				</>
			) : null}
			{current && current.multiValued ? (
				<div className='chart-builder__hint'>{`An item can hold several ${label} values and counts once in each, so the bars can add up to more than the items.`}</div>
			) : null}
		</BuilderStep>
	);
}
